import { PAYMENT_MEANS, type PaymentMean } from "@hakili/domain-types";
import { formatDateTime, formatMoney, t } from "@hakili/i18n";
import { Button, Card, Field, useDemandeFonctionnalite } from "@hakili/ui";
import { useEffect, useState } from "react";

import { libelleEcran } from "../shell/ConsoleShell.js";
import { roleWord } from "../shell/roles.js";
import type { Ecran, Vue } from "../shell/views.js";
import { VUE_DEFINITIONS } from "../shell/views.js";
import { MileageToleranceBlock, type MileageToleranceDoors } from "./tolerance-block.js";

/**
 * `P0-UIK-015`: the profile, `WF-CON-43`.
 *
 * `round CN-B`, `CN-B.3`: **to the drawing** (`hakili-console.jsx`,
 * `ProfilScreen`). The identity card with the initials disc, the person and
 * « Hakili · <vue> », « Modifier » on the name; the « Droits » block in the
 * drawing's own words per view , the rights table in code font goes, because
 * the drawing lists rights in words and a founder does not read a
 * permission code; the scope as the screens themselves stays, because the
 * drawing's `portee` is written per role and the list is that sentence's
 * table; « Cloisonnement »; « Une idée pour améliorer la console ? » opening
 * the same modal the rail opens, naming the screen the person was ON.
 *
 * **The administration's PAYMENT SETTINGS, by a drawing in words** , the
 * drawing places the administration's parameters on Profil and draws no
 * block for these; `WF-CLI-10` §6's three means, Hakili's rate, Hakili's
 * account for transfers, the published version with its publisher and date ,
 * read by `GET /payments/settings`, entered by `POST`, the door's refusal
 * rendered where the gesture is. Values are only ever typed by the
 * administrator; nothing here is seeded by the screen.
 */

export interface PaymentSettingsView {
  readonly version: number;
  readonly publishedBy: string | null;
  /** `round PAY-M`: the publisher's NAME, answered by the door from identity; the screen composes nothing. */
  readonly publishedByName: string | null;
  readonly publishedAt: string | null;
  readonly hakiliFeePct: number;
  readonly bankFeePct: number;
  /** `T-1c` (`P2-PAY-012`, I31 amended): the dealer's STANDARD commission rate on the sale price, 0 to 20. */
  readonly dealerCommissionPct?: number;
  /** `PAY-P`, `OD-29`: the two figures, the override note, and the guard's state. */
  readonly controlPriceAmount?: number | null;
  /** `FX-3`, `M2 (a)` (`D2`): the default fee Hakili pays a cabinet, `null` until it is entered. */
  readonly defaultCabinetFeeAmount?: number | null;
  readonly validReportFlatAmount?: number;
  readonly controlPriceOverrideNote?: string | null;
  readonly cabinetFeeCeiling?: { readonly cabinetName: string; readonly feeAmount: number } | null;
  /** The five in the record's order, each with its availability and, when unavailable, its reason. */
  readonly means: readonly {
    readonly code: string;
    readonly available: boolean;
    readonly reason: string | null;
  }[];
  readonly hakiliAccount: {
    readonly bankName: string | null;
    readonly accountName: string | null;
    readonly accountNumber: string | null;
  };
  /** 16 October 2026: one destination per method, each `null` until entered. */
  readonly destinations?: {
    readonly orange: { readonly number: string } | null;
    readonly moov: { readonly number: string } | null;
  };
}

/**
 * `round K-1`, `M3 (b)`: **the two terms of the buyer's clock**, versioned as
 * the fees' settings are, on their own door (`/api/v1/dossiers/hold-settings`,
 * row 27 by record). Version 0 is the founder's seed until his hand publishes.
 */
export interface HoldTermsView {
  readonly version: number;
  readonly buyerDaysTerm: number;
  readonly holdAgeDaysTerm: number;
  readonly publishedAt: string;
}

export interface HoldTermsDoors {
  readonly readHoldTerms: () => Promise<HoldTermsView>;
  readonly publishHoldTerms: (input: {
    buyerDaysTerm: number;
    holdAgeDaysTerm: number;
  }) => Promise<HoldTermsView>;
}

/** `round K-2` (resumed), `M4`: the sanctions' terms, `partnerships`' versioned row. */
export interface BreachTermsView {
  readonly version: number;
  readonly penaltyAmount: number | null;
  readonly demotionDays: number;
  readonly suspensionCount: number;
  readonly suspensionWindowDays: number;
  readonly publishedAt: string;
}

export interface BreachTermsDoors {
  readonly readBreachTerms: () => Promise<BreachTermsView>;
  readonly publishBreachTerms: (input: {
    penaltyAmount: number | null;
    demotionDays: number;
    suspensionCount: number;
    suspensionWindowDays: number;
  }) => Promise<BreachTermsView>;
}

export interface PaymentSettingsDoors {
  readonly readSettings: () => Promise<PaymentSettingsView>;
  readonly publishSettings: (input: {
    hakiliFeePct: number;
    /** `T-1c`: the dealer's standard rate; absent carries the standing value forward. */
    dealerCommissionPct?: number;
    /** `PAY-P`: the two figures; the price `null` withdraws it. */
    controlPriceAmount?: number | null;
    defaultCabinetFeeAmount?: number | null;
    validReportFlatAmount?: number;
    controlPriceOverrideNote?: string | null;
    orangeMoneyAvailable: boolean;
    waveMoneyAvailable: boolean;
    moovMoneyAvailable: boolean;
    samaMoneyAvailable: boolean;
    bankTransferAvailable: boolean;
    hakiliBankName: string;
    hakiliAccountName: string;
    hakiliAccountNumber: string;
    orangeMoneyNumber: string;
    moovMoneyNumber: string;
  }) => Promise<PaymentSettingsView>;
}

export interface ProfilProps {
  readonly nomComplet: string;
  readonly email: string;
  /** The role as the server named it. Displayed, never edited here. */
  readonly role: string;
  readonly vue: Vue;
  readonly organisation: string;
  /** The rights the SERVER granted, kept for the greyed controls elsewhere; not printed as a code table (CN-B.3). */
  readonly droits: Readonly<Record<string, string>>;
  readonly enregistrer: (nomComplet: string) => Promise<void>;
  readonly deconnecter: () => void;
  /** `parametrerPaiements`: the administration's block, or nothing. */
  readonly paiements?: PaymentSettingsDoors;
  /** `K-1`, `M3 (b)`: the reservation's two terms, beside the fees, the administration's. */
  readonly reservation?: HoldTermsDoors;
  /** `K-2` (resumed): the sanctions' terms beside the reservation's, the administration's block. */
  readonly manquement?: BreachTermsDoors;
  /** `P1-CNW-063` (TRANSITIONAL, beside the payment settings): Hakili's mileage tolerance, the administration's block. */
  readonly tolerance?: MileageToleranceDoors;
  /** The screen the person was on before Profil, for « Une idée ? »'s context. */
  readonly previousScreen?: string;
}

const DASH = t("common.valeur.tiret");
const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));
/** A figure grouped as `formatMoney` groups it, without the unit , for a sentence that names the unit itself. */
const grouped = (amount: number): string => formatMoney(amount).replace(/\s*FCFA$/u, "");

const initials = (name: string): string =>
  name
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

/** The drawing's « Droits » lines per view, as pairs of catalogue keys. */
const RIGHTS_LINES: Readonly<Record<Vue, readonly [Parameters<typeof t>[0], Parameters<typeof t>[0]][]>> = {
  admin: [
    ["console.profil.droitAdminOuvrir", "console.profil.droitAdminOuvrirValeur"],
    ["console.profil.droitAdminPremierCompte", "console.profil.droitAdminPremierCompteValeur"],
    ["console.profil.droitAdminDossiers", "console.profil.droitAdminDossiersValeur"],
    ["console.profil.droitAdminBancaires", "console.profil.droitAdminBancairesValeur"],
  ],
  resp: [
    ["console.profil.droitRespDossiers", "console.profil.droitRespDossiersValeur"],
    ["console.profil.droitRespEquipe", "console.profil.droitRespEquipeValeur"],
    ["console.profil.droitRespPortefeuille", "console.profil.droitRespPortefeuilleValeur"],
    ["console.profil.droitRespBanques", "console.profil.droitRespBanquesValeur"],
    ["console.profil.droitRespFinancieres", "console.profil.droitRespFinancieresValeur"],
  ],
  collecte: [
    ["console.profil.droitChargeDossiers", "console.profil.droitChargeDossiersValeur"],
    ["console.profil.droitChargePieces", "console.profil.droitChargePiecesValeur"],
    ["console.profil.droitChargeBanques", "console.profil.droitChargeBanquesValeur"],
    ["console.profil.droitChargeFinancieres", "console.profil.droitChargeFinancieresValeur"],
  ],
  banques: [
    ["console.profil.droitRelOuvrir", "console.profil.droitRelOuvrirBanques"],
    ["console.profil.droitRelPortefeuille", "console.profil.droitRelPortefeuilleValeur"],
    ["console.profil.droitRelPrefiltre", "console.profil.droitRelPrefiltreValeur"],
    ["console.profil.droitRelPremier", "console.profil.droitRelPremierValeur"],
    ["console.profil.droitRelDossiers", "console.profil.droitRelDossiersValeur"],
    ["console.profil.droitRelEspace", "console.profil.droitRelEspaceValeur"],
  ],
  concess: [
    ["console.profil.droitRelOuvrir", "console.profil.droitRelOuvrirConcess"],
    ["console.profil.droitRelPortefeuille", "console.profil.droitRelPortefeuilleValeur"],
    ["console.profil.droitRelQualite", "console.profil.droitRelQualiteValeur"],
    ["console.profil.droitRelPremier", "console.profil.droitRelPremierValeur"],
    ["console.profil.droitRelDossiers", "console.profil.droitRelDossiersValeur"],
    ["console.profil.droitRelEspace", "console.profil.droitRelEspaceValeur"],
  ],
};

export function Profil({
  nomComplet,
  email,
  role,
  vue,
  organisation,
  enregistrer,
  paiements,
  reservation,
  manquement,
  tolerance,
  previousScreen,
}: ProfilProps): React.JSX.Element {
  const [nom, setNom] = useState(nomComplet);
  const [editing, setEditing] = useState(false);
  const [etat, setEtat] = useState<"repos" | "envoi" | "enregistre">("repos");
  const demande = useDemandeFonctionnalite();

  async function soumettre(event: Pick<Event, "preventDefault">): Promise<void> {
    event.preventDefault();
    setEtat("envoi");
    try {
      await enregistrer(nom.trim());
      setEtat("enregistre");
      setEditing(false);
    } catch {
      setEtat("repos");
      throw new Error("profile update failed");
    }
  }

  const ecrans: readonly Ecran[] = VUE_DEFINITIONS[vue].ecrans;
  const vueLabel = t(VUE_DEFINITIONS[vue].labelKey);

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <h1 className="text-2xl font-semibold tracking-tight">{t("console.profil.titre")}</h1>

      <Card shape="console" className="flex items-center gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-indigo-700 text-lg font-semibold text-white">
          {initials(nomComplet)}
        </div>
        <div className="min-w-0 flex-1">
          {editing ? (
            <form
              className="flex flex-col gap-2"
              onSubmit={(event) => {
                void soumettre(event);
              }}
            >
              <Field label={t("console.profil.nom")} htmlFor="profil-nom">
                <input
                  id="profil-nom"
                  value={nom}
                  onChange={(event) => {
                    setNom(event.target.value);
                    setEtat("repos");
                  }}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                />
              </Field>
              <div className="flex items-center gap-2">
                <Button type="submit">{t("common.action.enregistrer")}</Button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setNom(nomComplet);
                  }}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  {t("console.equipe.annuler")}
                </button>
              </div>
            </form>
          ) : (
            <>
              <p className="text-base font-semibold">{nom}</p>
              <p className="text-sm text-gray-500">
                {organisation} · {vueLabel.toLowerCase()}
              </p>
              <p className="text-sm text-gray-500">
                {email} · {roleWord(role)}
              </p>
              {etat === "enregistre" ? (
                <p role="status" className="mt-1 text-xs text-emerald-700">
                  {t("console.profil.enregistre")}
                </p>
              ) : null}
            </>
          )}
        </div>
        {editing ? null : (
          <button
            type="button"
            data-testid="profil-modifier"
            onClick={() => {
              setEditing(true);
            }}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t("console.profil.modifier")}
          </button>
        )}
      </Card>

      <Card shape="console">
        <h2 className="mb-3 text-sm font-semibold">{t("console.profil.droits")}</h2>
        <div className="divide-y divide-gray-50">
          {RIGHTS_LINES[vue].map(([labelKey, valueKey]) => (
            <div key={labelKey} className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-sm text-gray-500">{t(labelKey)}</span>
              <span className="text-right text-sm text-gray-700">{t(valueKey)}</span>
            </div>
          ))}
        </div>
        {/* The scope, printed as the screens themselves: the drawing's `portee`
            is written per role, and the list is that sentence's table. */}
        <p className="mt-3 mb-2 text-xs text-gray-500">{t("console.profil.ecrans")}</p>
        <ul className="flex flex-wrap gap-2">
          {ecrans.map((ecran) => (
            <li key={ecran} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600">
              {libelleEcran(ecran)}
            </li>
          ))}
        </ul>
      </Card>

      <Card shape="console">
        <h2 className="mb-2 text-sm font-semibold">{t("console.profil.cloisonnementTitre")}</h2>
        <p className="text-sm leading-relaxed text-gray-600">{t("console.profil.cloisonnement")}</p>
      </Card>

      {paiements === undefined ? null : <PaymentSettingsBlock {...paiements} />}
      {reservation === undefined ? null : <HoldTermsBlock {...reservation} />}
      {manquement === undefined ? null : <BreachTermsBlock {...manquement} />}
      {tolerance === undefined ? null : <MileageToleranceBlock {...tolerance} />}

      <Card shape="console" className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold">{t("console.profil.ideeTitre")}</h2>
          <p className="mt-0.5 text-sm text-gray-500">{t("console.profil.ideeSous")}</p>
        </div>
        <button
          type="button"
          data-testid="profil-demander"
          onClick={() => {
            // WF-CON-43's acceptance criterion: the context names the screen the
            // person was ON before Profil, never « Profil » by default.
            demande.ouvrir(`console:${previousScreen ?? "profil"}:une-idee`);
          }}
          className="rounded-xl border border-indigo-100 bg-white px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50"
        >
          {t("console.profil.demanderFonctionnalite")}
        </button>
      </Card>
    </div>
  );
}

/**
 * `round PAY-M`: the five, keyed by the wire's own code (`09-01`); the ORDER
 * is `PAYMENT_MEANS`'s and typed nowhere here.
 */
const MEAN_WORDS = new Map<PaymentMean, Parameters<typeof t>[0]>([
  ["orange", "console.expertises.moyenOrange"],
  ["wave", "console.expertises.moyenWave"],
  ["moov", "console.expertises.moyenMoov"],
  ["sama", "console.expertises.moyenSama"],
  ["virement", "console.expertises.moyenVirement"],
]);

const NONE_ON: Readonly<Record<PaymentMean, boolean>> = {
  orange: false,
  wave: false,
  moov: false,
  sama: false,
  virement: false,
};

/** The administration's payment settings, by a drawing in words (`ADDENDUM-2026-09-12-console-admin.md`). */
/** `K-1`, `M3 (b)`: the reservation's two terms, in the payments block's own style. */
function HoldTermsBlock(doors: HoldTermsDoors): React.JSX.Element {
  const [terms, setTerms] = useState<HoldTermsView | null>(null);
  const [buyerDays, setBuyerDays] = useState("");
  const [ageDays, setAgeDays] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [published, setPublished] = useState(false);
  const { readHoldTerms } = doors;
  const seat = (view: HoldTermsView): void => {
    setTerms(view);
    setBuyerDays(String(view.buyerDaysTerm));
    setAgeDays(String(view.holdAgeDaysTerm));
  };
  useEffect(() => {
    let live = true;
    void readHoldTerms()
      .then((view) => {
        if (live) seat(view);
      })
      .catch((failure: unknown) => {
        if (live)
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      });
    return () => {
      live = false;
    };
  }, [readHoldTerms]);
  return (
    <Card shape="console">
      <h2 className="text-sm font-semibold">{t("console.profil.reservationTitre")}</h2>
      <p className="mt-0.5 mb-3 text-sm leading-relaxed text-gray-500">
        {t("console.profil.reservationSous")}
      </p>
      <p data-testid="reservation-version" className="mb-3 text-sm text-gray-500">
        {terms === null
          ? t("common.state.chargement")
          : terms.version === 0
            ? t("console.profil.reservationSeed")
            : t("console.profil.reservationVersion")
                .replace("{version}", String(terms.version))
                .replace("{date}", formatDateTime(new Date(terms.publishedAt)))}
      </p>
      <form
        data-testid="reservation-formulaire"
        className="grid grid-cols-2 gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void (async () => {
            setBusy(true);
            setRefusal(null);
            setPublished(false);
            try {
              seat(
                await doors.publishHoldTerms({
                  buyerDaysTerm: Number(buyerDays),
                  holdAgeDaysTerm: Number(ageDays),
                }),
              );
              setPublished(true);
            } catch (failure) {
              setRefusal(
                (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
              );
            } finally {
              setBusy(false);
            }
          })();
        }}
      >
        <label className="text-sm text-gray-600">
          {t("console.profil.reservationTermeClient")}
          <input
            data-testid="reservation-terme-client"
            inputMode="numeric"
            value={buyerDays}
            onChange={(event) => {
              setBuyerDays(event.target.value.replace(/\D/gu, ""));
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <label className="text-sm text-gray-600">
          {t("console.profil.reservationTermeTotal")}
          <input
            data-testid="reservation-terme-total"
            inputMode="numeric"
            value={ageDays}
            onChange={(event) => {
              setAgeDays(event.target.value.replace(/\D/gu, ""));
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <div className="col-span-2 flex items-center gap-3">
          <button
            type="submit"
            data-testid="reservation-publier"
            disabled={busy || terms === null || buyerDays.trim() === "" || ageDays.trim() === ""}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          >
            {t("console.profil.reservationPublier")}
          </button>
          {published ? (
            <p role="status" data-testid="reservation-publie" className="text-xs text-emerald-700">
              {t("console.profil.reservationPublie")}
            </p>
          ) : null}
        </div>
      </form>
      {refusal === null ? null : (
        <p role="alert" data-testid="reservation-refus" className="mt-2 text-sm text-rose-600">
          {refusal}
        </p>
      )}
    </Card>
  );
}

/**
 * `round K-2` (resumed), `M4` (WF-CON-43 amended, `DECISION ADDED 08-17`): **« Manquement à la
 * réservation »**, the sanctions' terms beside the reservation's , the fixed penalty (a dash and an
 * empty field while the founder has not decided it, R81; published only with a figure), the
 * demotion's days, the suspension's count and window , by row 27's door family
 * (`POST /api/v1/partners/breach-settings`), versioned, never a constant.
 */
function BreachTermsBlock(doors: BreachTermsDoors): React.JSX.Element {
  const [terms, setTerms] = useState<BreachTermsView | null>(null);
  const [penalty, setPenalty] = useState("");
  const [demotion, setDemotion] = useState("");
  const [count, setCount] = useState("");
  const [window, setWindow] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [published, setPublished] = useState(false);
  const { readBreachTerms } = doors;
  const seat = (view: BreachTermsView): void => {
    setTerms(view);
    setPenalty(view.penaltyAmount === null ? "" : String(view.penaltyAmount));
    setDemotion(String(view.demotionDays));
    setCount(String(view.suspensionCount));
    setWindow(String(view.suspensionWindowDays));
  };
  useEffect(() => {
    let live = true;
    void readBreachTerms()
      .then((view) => {
        if (live) seat(view);
      })
      .catch((failure: unknown) => {
        if (live)
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      });
    return () => {
      live = false;
    };
  }, [readBreachTerms]);
  const digits = (value: string): string => value.replace(/\D/gu, "");
  const field = (
    key: Parameters<typeof t>[0],
    testId: string,
    value: string,
    set: (next: string) => void,
  ): React.JSX.Element => (
    <label className="text-sm text-gray-600">
      {t(key)}
      <input
        data-testid={testId}
        inputMode="numeric"
        value={value}
        onChange={(event) => {
          set(digits(event.target.value));
        }}
        className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
      />
    </label>
  );
  return (
    <Card shape="console">
      <h2 className="text-sm font-semibold">{t("console.profil.manquementTitre")}</h2>
      <p className="mt-0.5 mb-3 text-sm leading-relaxed text-gray-500">
        {t("console.profil.manquementSous")}
      </p>
      <p data-testid="manquement-version" className="mb-3 text-sm text-gray-500">
        {terms === null
          ? t("common.state.chargement")
          : terms.version === 0
            ? t("console.profil.manquementSeed")
            : t("console.profil.manquementVersion")
                .replace("{version}", String(terms.version))
                .replace("{date}", formatDateTime(new Date(terms.publishedAt)))}
      </p>
      <p data-testid="manquement-penalite-lecture" className="mb-3 text-sm text-gray-700">
        {t("console.profil.manquementPenalite")} :{" "}
        <span className="font-medium tabular-nums">
          {terms?.penaltyAmount === null || terms?.penaltyAmount === undefined
            ? DASH
            : formatMoney(terms.penaltyAmount)}
        </span>
      </p>
      <form
        data-testid="manquement-formulaire"
        className="grid grid-cols-2 gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void (async () => {
            setBusy(true);
            setRefusal(null);
            setPublished(false);
            try {
              seat(
                await doors.publishBreachTerms({
                  penaltyAmount: penalty.trim() === "" ? null : Number(penalty),
                  demotionDays: Number(demotion),
                  suspensionCount: Number(count),
                  suspensionWindowDays: Number(window),
                }),
              );
              setPublished(true);
            } catch (failure) {
              setRefusal(
                (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
              );
            } finally {
              setBusy(false);
            }
          })();
        }}
      >
        {field("console.profil.manquementPenalite", "manquement-penalite", penalty, setPenalty)}
        {field("console.profil.manquementRetrogradation", "manquement-retrogradation", demotion, setDemotion)}
        {field("console.profil.manquementSuspensionNombre", "manquement-suspension-nombre", count, setCount)}
        {field(
          "console.profil.manquementSuspensionFenetre",
          "manquement-suspension-fenetre",
          window,
          setWindow,
        )}
        <div className="col-span-2 flex items-center gap-3">
          <button
            type="submit"
            data-testid="manquement-publier"
            disabled={
              busy || terms === null || demotion.trim() === "" || count.trim() === "" || window.trim() === ""
            }
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          >
            {t("console.profil.manquementPublier")}
          </button>
          {published ? (
            <p role="status" data-testid="manquement-publie" className="text-xs text-emerald-700">
              {t("console.profil.manquementPublie")}
            </p>
          ) : null}
        </div>
      </form>
      {refusal === null ? null : (
        <p role="alert" data-testid="manquement-refus" className="mt-2 text-sm text-rose-600">
          {refusal}
        </p>
      )}
    </Card>
  );
}

function PaymentSettingsBlock(doors: PaymentSettingsDoors): React.JSX.Element {
  const [settings, setSettings] = useState<PaymentSettingsView | null>(null);
  const [fee, setFee] = useState("");
  const [price, setPrice] = useState("");
  const [reduced, setReduced] = useState("");
  // `FX-3`, `M2 (a)`: the second amount of `D3`, kept as typed text so an empty field can WITHDRAW it.
  const [cabinetFee, setCabinetFee] = useState("");
  const [note, setNote] = useState("");
  const [means, setMeans] = useState<Readonly<Record<PaymentMean, boolean>>>(NONE_ON);
  const [bankName, setBankName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [orangeNumber, setOrangeNumber] = useState("");
  const [moovNumber, setMoovNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [published, setPublished] = useState(false);
  // `T-1c`: « Commission concessionnaire : N % », the standard every dealer reads unless a term stands.
  const [dealerRate, setDealerRate] = useState("");
  const { readSettings } = doors;

  const seat = (view: PaymentSettingsView): void => {
    setSettings(view);
    setFee(String(view.hakiliFeePct));
    setDealerRate(view.dealerCommissionPct === undefined ? "" : String(view.dealerCommissionPct));
    setPrice(
      view.controlPriceAmount === undefined || view.controlPriceAmount === null
        ? ""
        : String(view.controlPriceAmount),
    );
    setReduced(view.validReportFlatAmount === undefined ? "" : String(view.validReportFlatAmount));
    setCabinetFee(
      view.defaultCabinetFeeAmount === undefined || view.defaultCabinetFeeAmount === null
        ? ""
        : String(view.defaultCabinetFeeAmount),
    );
    setNote(view.controlPriceOverrideNote ?? "");
    setMeans(
      Object.fromEntries(
        PAYMENT_MEANS.map((code) => [code, view.means.find((one) => one.code === code)?.available ?? false]),
      ) as Record<PaymentMean, boolean>,
    );
    setBankName(view.hakiliAccount.bankName ?? "");
    setAccountName(view.hakiliAccount.accountName ?? "");
    setAccountNumber(view.hakiliAccount.accountNumber ?? "");
    setOrangeNumber(view.destinations?.orange?.number ?? "");
    setMoovNumber(view.destinations?.moov?.number ?? "");
  };

  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const answered = await readSettings();
        if (run.live) seat(answered);
      } catch (failure) {
        if (run.live)
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readSettings]);

  /**
   * `FX-3`, `M2 (a)`: the gap between the two amounts, which `D1` calls Hakili's margin, said in words.
   * Computed here rather than in the markup so the two nullable figures are narrowed ONCE , and a dash
   * while either is unentered, because a zero would be a claim about a number nobody has published.
   */
  const publishedPrice = settings?.controlPriceAmount ?? null;
  const cabinetDefault = settings?.defaultCabinetFeeAmount ?? null;
  const margin =
    publishedPrice === null || cabinetDefault === null
      ? DASH
      : publishedPrice >= cabinetDefault
        ? fill("console.profil.paiementsMarge", { amount: grouped(publishedPrice - cabinetDefault) })
        : fill("console.profil.paiementsMargeNegative", {
            amount: grouped(cabinetDefault - publishedPrice),
          });

  return (
    <Card shape="console">
      <h2 className="text-sm font-semibold">{t("console.profil.paiementsTitre")}</h2>
      <p className="mt-0.5 mb-3 text-sm text-gray-500">{t("console.profil.paiementsSous")}</p>
      <p data-testid="paiements-version" className="mb-3 text-sm text-gray-500">
        {settings === null
          ? t("common.state.chargement")
          : settings.publishedAt === null
            ? t("console.profil.paiementsVersionSeed").replace("{version}", String(settings.version))
            : t("console.profil.paiementsVersion")
                .replace("{version}", String(settings.version))
                .replace("{par}", settings.publishedByName ?? settings.publishedBy ?? DASH)
                .replace("{date}", formatDateTime(new Date(settings.publishedAt)))}
      </p>
      <form
        data-testid="paiements-formulaire"
        className="grid grid-cols-2 gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void (async () => {
            setBusy(true);
            setRefusal(null);
            setPublished(false);
            try {
              const answered = await doors.publishSettings({
                hakiliFeePct: Number(fee),
                ...(dealerRate.trim() === "" ? {} : { dealerCommissionPct: Number(dealerRate) }),
                // `PAY-P`: an empty price WITHDRAWS it (an entry with no value again).
                controlPriceAmount: price.trim() === "" ? null : Number(price),
                ...(reduced.trim() === "" ? {} : { validReportFlatAmount: Number(reduced) }),
                // `FX-3`, `M2 (a)`: an empty field WITHDRAWS the default, as the control's price does.
                defaultCabinetFeeAmount: cabinetFee.trim() === "" ? null : Number(cabinetFee),
                controlPriceOverrideNote: note.trim() === "" ? null : note.trim(),
                orangeMoneyAvailable: means.orange,
                waveMoneyAvailable: means.wave,
                moovMoneyAvailable: means.moov,
                samaMoneyAvailable: means.sama,
                bankTransferAvailable: means.virement,
                hakiliBankName: bankName.trim(),
                hakiliAccountName: accountName.trim(),
                hakiliAccountNumber: accountNumber.trim(),
                // 16 October 2026: each method's own destination, entered where the bank account is.
                orangeMoneyNumber: orangeNumber.trim(),
                moovMoneyNumber: moovNumber.trim(),
              });
              seat(answered);
              setPublished(true);
            } catch (failure) {
              setRefusal(
                (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
              );
            } finally {
              setBusy(false);
            }
          })();
        }}
      >
        <label className="text-sm text-gray-600">
          {t("console.profil.paiementsTaux")}
          <input
            data-testid="paiements-taux"
            inputMode="decimal"
            value={fee}
            onChange={(event) => {
              setFee(event.target.value);
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        {/* `T-1c` (`P2-PAY-012`, I31 amended by the founder's decision of 18 September): the
            dealer's standard commission rate on the sale price, published by this door with the
            same versioning; a negotiated term on a dealer's convention is read before it. */}
        <label className="text-sm text-gray-600">
          {t("console.profil.paiementsCommission")}
          <input
            data-testid="paiements-commission"
            inputMode="numeric"
            value={dealerRate}
            onChange={(event) => {
              setDealerRate(event.target.value.replace(/\D/gu, ""));
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <p data-testid="paiements-commission-note" className="col-span-2 text-sm text-gray-500">
          {t("console.profil.paiementsCommissionNote")}
        </p>
        {/* `PAY-P`, `OD-29`: the control's ONE price and the reduced price, the
            guard's state in words beside them , what the fiche prints, and
            what the door will refuse. */}
        <label className="text-sm text-gray-600">
          {t("console.profil.paiementsTarif")}
          <input
            data-testid="paiements-tarif"
            inputMode="numeric"
            value={price}
            onChange={(event) => {
              setPrice(event.target.value.replace(/\D/gu, ""));
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <label className="text-sm text-gray-600">
          {t("console.profil.paiementsTarifReduit")}
          <input
            data-testid="paiements-tarif-reduit"
            inputMode="numeric"
            value={reduced}
            onChange={(event) => {
              setReduced(event.target.value.replace(/\D/gu, ""));
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <p data-testid="paiements-tarif-garde" className="col-span-2 text-sm text-gray-500">
          {settings === null
            ? DASH
            : [
                settings.controlPriceAmount === undefined || settings.controlPriceAmount === null
                  ? t("console.profil.paiementsTarifNonPublie")
                  : fill("console.profil.paiementsTarifPublie", {
                      amount: grouped(settings.controlPriceAmount),
                    }),
                settings.controlPriceOverrideNote === undefined || settings.controlPriceOverrideNote === null
                  ? null
                  : fill("console.profil.paiementsTarifDerogation", {
                      note: settings.controlPriceOverrideNote,
                    }),
                settings.cabinetFeeCeiling === undefined || settings.cabinetFeeCeiling === null
                  ? t("console.profil.paiementsGardeAucun")
                  : fill("console.profil.paiementsGardePlafond", {
                      amount: grouped(settings.cabinetFeeCeiling.feeAmount),
                      cabinet: settings.cabinetFeeCeiling.cabinetName,
                    }),
              ]
                .filter((line) => line !== null)
                .join(" ")}
        </p>
        {/* `FX-3`, `M2 (a)` (`D2`, `D3`): the second amount, beside the first, with the margin between
            the two said in words , the gap IS Hakili's margin (`D1`), and it is the one number the
            administration cannot read anywhere else. */}
        <label className="text-sm text-gray-600">
          {t("console.profil.paiementsHonoraires")}
          <input
            data-testid="paiements-honoraires"
            inputMode="numeric"
            value={cabinetFee}
            onChange={(event) => {
              setCabinetFee(event.target.value.replace(/\D/gu, ""));
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <p data-testid="paiements-honoraires-aide" className="text-sm text-gray-500">
          {t("console.profil.paiementsHonorairesAide")}
        </p>
        <p data-testid="paiements-honoraires-etat" className="text-sm text-gray-500">
          {settings === null
            ? DASH
            : settings.defaultCabinetFeeAmount === undefined || settings.defaultCabinetFeeAmount === null
              ? t("console.profil.paiementsHonorairesNonPublies")
              : fill("console.profil.paiementsHonorairesPublies", {
                  amount: grouped(settings.defaultCabinetFeeAmount),
                })}
        </p>
        <p data-testid="paiements-marge" className="col-span-2 text-xs text-gray-500">
          {margin}
        </p>
        <label className="col-span-2 text-sm text-gray-600">
          {t("console.profil.paiementsMotif")}
          <input
            data-testid="paiements-motif"
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <div className="text-xs text-gray-600">
          {t("console.profil.paiementsMoyens")}
          <div className="mt-1 flex flex-col gap-1">
            {/* The five, in the record's order; a means whose provider is not
                connected is refused by the door in words, and the refusal is
                rendered below, where the gesture is (`PAY-M.1`). */}
            {PAYMENT_MEANS.map((code) => {
              const key = MEAN_WORDS.get(code);
              return (
                <label key={code} className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    data-testid={`paiements-moyen-${code}`}
                    checked={means[code]}
                    onChange={(event) => {
                      setMeans({ ...means, [code]: event.target.checked });
                    }}
                  />
                  {key === undefined ? code : t(key)}
                </label>
              );
            })}
          </div>
        </div>
        <label className="text-sm text-gray-600">
          {t("console.profil.paiementsBanque")}
          <input
            data-testid="paiements-banque"
            value={bankName}
            onChange={(event) => {
              setBankName(event.target.value);
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <label className="text-sm text-gray-600">
          {t("console.profil.paiementsTitulaire")}
          <input
            data-testid="paiements-titulaire"
            value={accountName}
            onChange={(event) => {
              setAccountName(event.target.value);
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <label className="col-span-2 text-sm text-gray-600">
          {t("console.profil.paiementsNumero")}
          <input
            data-testid="paiements-numero"
            value={accountNumber}
            onChange={(event) => {
              setAccountNumber(event.target.value);
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <label className="col-span-2 text-sm text-gray-600">
          {t("console.profil.paiementsNumeroOrange")}
          <input
            data-testid="paiements-numero-orange"
            inputMode="tel"
            value={orangeNumber}
            onChange={(event) => {
              setOrangeNumber(event.target.value);
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <label className="col-span-2 text-sm text-gray-600">
          {t("console.profil.paiementsNumeroMoov")}
          <input
            data-testid="paiements-numero-moov"
            inputMode="tel"
            value={moovNumber}
            onChange={(event) => {
              setMoovNumber(event.target.value);
            }}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
          />
        </label>
        <div className="col-span-2 flex items-center gap-3">
          <button
            type="submit"
            data-testid="paiements-publier"
            disabled={busy || settings === null || fee.trim() === ""}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          >
            {t("console.profil.paiementsPublier")}
          </button>
          {published ? (
            <p role="status" data-testid="paiements-publie" className="text-xs text-emerald-700">
              {t("console.profil.paiementsPublie")}
            </p>
          ) : null}
        </div>
      </form>
      {refusal === null ? null : (
        <p role="alert" data-testid="paiements-refus" className="mt-2 text-sm text-rose-600">
          {refusal}
        </p>
      )}
      <p className="mt-3 text-sm leading-relaxed text-gray-500">{t("console.profil.paiementsNote")}</p>
    </Card>
  );
}
